import { WidgetValues } from '../types';

type KPI = {
  name: string;
  description: string;
};

type WidgetTooltipConfig = {
  whyUse: string;
  kpis: KPI[];
};

export const WIDGET_TOOLTIP_CONFIG: Record<WidgetValues, WidgetTooltipConfig> =
  {
    [WidgetValues.MY_TEAM_STATS]: {
      whyUse:
        "Choose this for a high-level overview of your team's core activity, tracking total videos recorded, sent, and viewed at a glance.",
      kpis: [
        {
          name: 'Recorded Videos',
          description: 'Number of personalized videos recorded by staff',
        },
        {
          name: 'Sent',
          description: 'Total amount of videos sent to recipients',
        },
        {
          name: 'Total Views',
          description: 'Cumulative number of views across all videos',
        },
      ],
    },

    [WidgetValues.VIDEO_DELIVERY]: {
      whyUse:
        'Use this to monitor your personalized video outreach from creation to conversion, helping you measure effectiveness and viewer impact at a glance.',
      kpis: [
        {
          name: 'Recorded',
          description: 'Number of personalized videos recorded by staff',
        },
        {
          name: 'Unique Sent',
          description:
            'Total distinct videos sent, excluding repeat sends of the same video.',
        },
        {
          name: 'Viewed',
          description:
            'Cumulative number of views across the unique videos sent.',
        },
        {
          name: 'Average Engagement Rate',
          description:
            "The percentage of each video's total duration that has been viewed. If applied to more than one video, it is averaged across all videos within the selected time period.",
        },
        {
          name: 'Total CTA Clicks',
          description:
            'Count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.VIDEO_ENGAGEMENTS]: {
      whyUse:
        'Select this to understand viewer behavior. It tracks how long people watch the video and whether they are interacting with your Call-to-Action (CTA) buttons.',
      kpis: [
        {
          name: 'Views',
          description: 'Cumulative number of views across all videos',
        },
        {
          name: 'Engagement',
          description:
            "The percentage of each video's total duration that has been viewed. If applied to more than one video, it is averaged across all videos within the selected time period.",
        },
        {
          name: 'CTA Clicks',
          description:
            'Count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.NEW_VIDEOS]: {
      whyUse:
        'Choose this to monitor content creation trends, specifically seeing the ratio of fresh recordings versus duplicated or merged content.',
      kpis: [
        {
          name: 'New Recorded',
          description:
            'Percentage of total videos that are newly recorded videos',
        },
        {
          name: 'Duplicated',
          description: 'Percentage of total videos that are duplicate videos',
        },
        {
          name: 'Merged',
          description:
            'Percentage of total videos created by merging two or more videos together',
        },
        {
          name: 'Is Demo Video',
          description: 'Percentage of total videos marked as demo videos',
        },
      ],
    },

    [WidgetValues.DELIVERY_METHOD]: {
      whyUse:
        'Use this to identify how your team is sending videos, comparing the volume of sends across Email, SMS, and direct links.',
      kpis: [
        {
          name: 'Other',
          description:
            'Count of videos sent using the Copy Video/Send option in Covideo',
        },
        {
          name: 'Emails',
          description: 'Count of videos sent via email through Covideo',
        },
        {
          name: 'SMS',
          description: 'Count of videos sent via SMS through Covideo',
        },
      ],
    },

    [WidgetValues.VIEW_RATE_BY_DELIVERY_METHOD]: {
      whyUse:
        'Select this to determine your most effective channel. It reveals which delivery method (Email vs. SMS) results in the highest open rates.',
      kpis: [
        {
          name: 'Other',
          description:
            'Percentage of videos sent using the Copy Video/Send option in Covideo',
        },
        {
          name: 'Emails',
          description: 'Percentage of videos sent via email through Covideo',
        },
        {
          name: 'SMS',
          description: 'Percentage of videos sent via SMS through Covideo',
        },
      ],
    },

    [WidgetValues.TOP_VIDEOS]: {
      whyUse:
        'Use this to identify your "greatest hits." It highlights which specific videos are generating the most cumulative views and engagement.',
      kpis: [
        {
          name: 'Views',
          description: 'Cumulative number of views across all videos',
        },
        {
          name: 'Engagement',
          description:
            "The percentage of each video's total duration that has been viewed. If applied to more than one video, it is averaged across all videos within the selected time period.",
        },
        {
          name: 'CTA Clicks',
          description:
            'Count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.TOP_USERS]: {
      whyUse:
        'Choose this to identify power users within your organization by tracking who is recording the most and getting the best results.',
      kpis: [
        {
          name: 'User',
          description: 'Individual user associated with the listed metrics.',
        },
        {
          name: 'Recorded',
          description: 'Number of personalized videos recorded by staff',
        },
        {
          name: 'Engagement',
          description:
            "The percentage of each video's total duration that has been viewed. If applied to more than one video, it is averaged across all videos within the selected time period.",
        },
        {
          name: 'Views',
          description: 'Cumulative number of views across all videos',
        },
        {
          name: 'Clicks',
          description:
            'Count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.TOP_CTA_SETS]: {
      whyUse:
        'Select this to optimize your conversion strategy. It shows which groups of buttons are driving the most clicks on your video landing pages.',
      kpis: [
        {
          name: 'CTA Set',
          description:
            'A group of call-to-action buttons named and saved as a set.',
        },
        {
          name: 'Most Clicked Link',
          description:
            'The most clicked call-to-action button on the video landing page.',
        },
        {
          name: 'Total Set Clicks',
          description: 'Total clicks for the top call-to-action set.',
        },
      ],
    },

    [WidgetValues.TOP_FIVE_USER_COMPARISON]: {
      whyUse:
        'Use this for performance benchmarking, allowing you to compare the engagement and view rates of your top five most active users.',
      kpis: [
        {
          name: 'Engagement',
          description:
            "The percentage of each video's total duration that has been viewed. If applied to more than one video, it is averaged across all videos within the selected time period.",
        },
        {
          name: 'Views',
          description: 'Cumulative number of views across all videos',
        },
        {
          name: 'CTA Clicks',
          description:
            'Count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.DEPARTMENTS]: {
      whyUse:
        'Choose this to see cross-functional performance, breaking down video impact and engagement by specific departmental teams.',
      kpis: [
        {
          name: 'Engagement',
          description:
            "By department, the percentage of each video's total duration viewed. If applied to more than one video, it is averaged across all videos within the selected time period.",
        },
        {
          name: 'Views',
          description:
            'By department, the cumulative number of views across all videos',
        },
        {
          name: 'CTA Clicks',
          description:
            'By department, the count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.TOP_CTA_LINKS]: {
      whyUse:
        'Select this to find out exactly what your customers are clicking. It tracks the success of individual links tied to your action buttons.',
      kpis: [
        {
          name: 'CTA Link',
          description:
            'A unique link tied to a call-to-action button on the video landing page.',
        },
        {
          name: 'CTA Set',
          description:
            'A group of call-to-action buttons named and saved as a set.',
        },
        {
          name: 'Clicks',
          description:
            'Count of clicks on call-to-action buttons on the video landing page',
        },
      ],
    },

    [WidgetValues.VIEWS_BY_VIDEO_TYPE]: {
      whyUse:
        'Use this to see your content distribution across categories like Prospecting, Marketing, or TYFB.',
      kpis: [
        {
          name: 'Marketing',
          description: 'Type of video categorized as Marketing',
        },
        {
          name: 'Respond to Inquiry',
          description: 'Type of video categorized as Respond to Inquiry',
        },
        {
          name: 'Prospecting',
          description: 'Type of video categorized as Prospecting',
        },
        {
          name: 'TYFB',
          description:
            'Type of video categorized as Thank You For Your Business',
        },
        { name: 'Other', description: 'Type of video categorized as Other' },
      ],
    },

    [WidgetValues.LEADERBOARD]: {
      whyUse:
        'Choose this to drive healthy competition by ranking individual users based on their total sends, recordings, and views.',
      kpis: [
        {
          name: 'User',
          description: 'Individual user associated with the listed metrics.',
        },
        {
          name: 'Videos Sent',
          description: 'Number of videos sent to recipients',
        },
        {
          name: 'Recorded',
          description: 'Number of personalized videos recorded by staff',
        },
        {
          name: 'Views',
          description: 'Cumulative number of views across all videos',
        },
      ],
    },
  };
